import { useState, useEffect } from 'react';

interface OrderData {
  // Window services
  yksilasinenIkkuna: number;
  kaksilasinenIkkuna: number;
  kolmilasinenIkkuna: number;
  tuuletusikkuna: number;
  kaksilasinenTuuletusikkuna: number;
  ruutuikkuna: number;
  ikkunaryhma: number;
  oviLasilla: number;
  kanTyvaParvekelasi: number;
  // Additional services
  gardenWatering: boolean;
  grassCut: boolean;
  // Customer info
  name: string;
  address: string;
  city: string;
  phone: string;
  email: string;
  notes: string;
}

const PRICES = {
  yksilasinenIkkuna: 10,
  kaksilasinenIkkuna: 20,
  kolmilasinenIkkuna: 30,
  tuuletusikkuna: 10,
  kaksilasinenTuuletusikkuna: 20,
  ruutuikkuna: 15,
  ikkunaryhma: 22,
  oviLasilla: 10,
  kanTyvaParvekelasi: 7,
  gardenWatering: 15,
  grassCut: 15,
};

// Product images
const imgYksilasinenIkkuna = "https://public.youware.com/users-website-assets/prod/cee6962b-a047-4e09-8560-bc95731a1639/8f113b7623714faa895876fa20215bb0.png";
const imgKaksilasinenIkkuna = "https://public.youware.com/users-website-assets/prod/cee6962b-a047-4e09-8560-bc95731a1639/8f113b7623714faa895876fa20215bb0.png";
const imgKolmilasinenIkkuna = "https://public.youware.com/users-website-assets/prod/cee6962b-a047-4e09-8560-bc95731a1639/105c5377dc6f4a2b908b67ee88eaad0d.png";
const imgTuuletusikkuna = "https://public.youware.com/users-website-assets/prod/cee6962b-a047-4e09-8560-bc95731a1639/4b2eb17015bb4a8c8e862828bfe8f34f.png";
const imgRuutuikkuna = "https://public.youware.com/users-website-assets/prod/cee6962b-a047-4e09-8560-bc95731a1639/ee7b8f1fd9a64fa888066d17b1d9a427.png";
const imgIkkunaryhma = "https://public.youware.com/users-website-assets/prod/cee6962b-a047-4e09-8560-bc95731a1639/9ea0fbd537bc48c1bfd6ddf343e3886e.png";
const imgOviLasilla = "https://public.youware.com/users-website-assets/prod/cee6962b-a047-4e09-8560-bc95731a1639/30cda218cfce4ea8add286f9ebb8abbf.png";
const imgKanTyvaParvekelasi = "https://washup.fi/wp-content/uploads/avautuvat-parvekelasit.png";

// Hero section image - custom Joona window cleaning photo
const windowImg1 = "/joona-window-cleaning.jpg";
const windowImg2 = "https://public.youware.com/users-website-assets/prod/cee6962b-a047-4e09-8560-bc95731a1639/caf9efaeb8824e8e8fdcbb0907b68b2b.jpg";
const windowImg3 = "https://public.youware.com/users-website-assets/prod/cee6962b-a047-4e09-8560-bc95731a1639/84957dbe8dd84a2c8953accd1beb9bd6.jpg";

function App() {
  const [formData, setFormData] = useState<OrderData>({
    yksilasinenIkkuna: 0,
    kaksilasinenIkkuna: 0,
    kolmilasinenIkkuna: 0,
    tuuletusikkuna: 0,
    kaksilasinenTuuletusikkuna: 0,
    ruutuikkuna: 0,
    ikkunaryhma: 0,
    oviLasilla: 0,
    kanTyvaParvekelasi: 0,
    gardenWatering: false,
    grassCut: false,
    name: '',
    address: '',
    city: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const calculateTotal = () => {
    const windowTotal = 
      (formData.yksilasinenIkkuna * PRICES.yksilasinenIkkuna) +
      (formData.kaksilasinenIkkuna * PRICES.kaksilasinenIkkuna) +
      (formData.kolmilasinenIkkuna * PRICES.kolmilasinenIkkuna) +
      (formData.tuuletusikkuna * PRICES.tuuletusikkuna) +
      (formData.kaksilasinenTuuletusikkuna * PRICES.kaksilasinenTuuletusikkuna) +
      (formData.ruutuikkuna * PRICES.ruutuikkuna) +
      (formData.ikkunaryhma * PRICES.ikkunaryhma) +
      (formData.oviLasilla * PRICES.oviLasilla) +
      (formData.kanTyvaParvekelasi * PRICES.kanTyvaParvekelasi);
    const additionalTotal = (formData.gardenWatering ? PRICES.gardenWatering : 0) + (formData.grassCut ? PRICES.grassCut : 0);
    return windowTotal + additionalTotal;
  };

  // Duration calculation: 5 min per window
  const calculateDuration = () => {
    const totalWindows = 
      formData.yksilasinenIkkuna +
      formData.kaksilasinenIkkuna +
      formData.kolmilasinenIkkuna +
      formData.tuuletusikkuna +
      formData.kaksilasinenTuuletusikkuna +
      formData.ruutuikkuna +
      formData.ikkunaryhma +
      formData.oviLasilla +
      formData.kanTyvaParvekelasi;
    
    const minutes = totalWindows * 6;
    
    return minutes;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setEmailError('');

    // Validate email match
    if (formData.email !== confirmEmail) {
      setEmailError('Sähköpostit eivät täsmää!');
      setIsSubmitting(false);
      return;
    }

    // Build ordered products list
    const orderedProducts = [];
    if (formData.yksilasinenIkkuna > 0) orderedProducts.push({ name: 'Yksilasinen ikkuna', quantity: formData.yksilasinenIkkuna, price: PRICES.yksilasinenIkkuna });
    if (formData.kaksilasinenIkkuna > 0) orderedProducts.push({ name: 'Kaksilasinen ikkuna', quantity: formData.kaksilasinenIkkuna, price: PRICES.kaksilasinenIkkuna });
    if (formData.kolmilasinenIkkuna > 0) orderedProducts.push({ name: 'Kolmilasinen ikkuna', quantity: formData.kolmilasinenIkkuna, price: PRICES.kolmilasinenIkkuna });
    if (formData.tuuletusikkuna > 0) orderedProducts.push({ name: 'Tuuletusikkuna', quantity: formData.tuuletusikkuna, price: PRICES.tuuletusikkuna });
    if (formData.kaksilasinenTuuletusikkuna > 0) orderedProducts.push({ name: 'Kaksilasinen tuuletusikkuna', quantity: formData.kaksilasinenTuuletusikkuna, price: PRICES.kaksilasinenTuuletusikkuna });
    if (formData.ruutuikkuna > 0) orderedProducts.push({ name: 'Ruutuikkuna', quantity: formData.ruutuikkuna, price: PRICES.ruutuikkuna });
    if (formData.ikkunaryhma > 0) orderedProducts.push({ name: 'Ikkunaryhmä', quantity: formData.ikkunaryhma, price: PRICES.ikkunaryhma });
    if (formData.oviLasilla > 0) orderedProducts.push({ name: 'Ovi lasilla', quantity: formData.oviLasilla, price: PRICES.oviLasilla });
    if (formData.kanTyvaParvekelasi > 0) orderedProducts.push({ name: 'Kännyvä parvekelasi', quantity: formData.kanTyvaParvekelasi, price: PRICES.kanTyvaParvekelasi });
    if (formData.gardenWatering) orderedProducts.push({ name: 'Puutarhan kastelu', quantity: 1, price: PRICES.gardenWatering });
    if (formData.grassCut) orderedProducts.push({ name: 'Nurmikon leikkaus', quantity: 1, price: PRICES.grassCut });

    try {
      const response = await fetch('https://www.joonanikkunanpesu.fi/tilaus.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          totalPrice: calculateTotal(),
          orderedProducts: orderedProducts,
        }),
      });

      // Parse JSON response
      const data = await response.json();
      
      // Check for "ok" response
      if (response.ok && data.status === 'ok') {
        setSubmitStatus('success');
        setConfirmEmail('');
        setFormData({
          yksilasinenIkkuna: 0,
          kaksilasinenIkkuna: 0,
          kolmilasinenIkkuna: 0,
          tuuletusikkuna: 0,
          kaksilasinenTuuletusikkuna: 0,
          ruutuikkuna: 0,
          ikkunaryhma: 0,
          oviLasilla: 0,
          kanTyvaParvekelasi: 0,
          gardenWatering: false,
          grassCut: false,
          name: '',
          address: '',
          city: '',
          phone: '',
          email: '',
          notes: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = <K extends keyof OrderData>(field: K, value: OrderData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50">
      {/* Hero Section */}
      <header className="relative h-[650px] md:h-[750px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={windowImg1}
            alt="Ikkunanpesu"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-black/20"></div>
        </div>
        <a
          href="tel:0453176464"
          className="absolute top-4 right-4 z-20 flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-sm font-medium py-1.5 px-3 rounded-lg transition"
        >
          <span></span>
          <span>0453176464</span>
        </a>
        <div
          className="relative z-10 h-full flex flex-col justify-center items-center text-center"
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "16px",
            fontWeight: "400",
            color: "rgb(0, 0, 0)",
            textAlign: "center",
            backgroundColor: "rgba(0, 0, 0, 0)",
            borderRadius: "0px",
            margin: "0px",
            padding: "20px",
            marginTop: "0px",
            marginRight: "0px",
            marginBottom: "0px",
            marginLeft: "0px",
            paddingTop: "20px",
            paddingRight: "20px",
            paddingBottom: "20px",
            paddingLeft: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: "100%"
          }}>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 drop-shadow-lg"
            style={{
              fontSize: "clamp(28px, 5vw, 56px)",
              fontWeight: "800",
              color: "rgb(255, 255, 255)",
              textAlign: "center",
              backgroundColor: "transparent",
              borderRadius: "0px",
              margin: "0px 10px 16px 10px",
              padding: "0px",
              marginTop: "0px",
              marginRight: "10px",
              marginBottom: "16px",
              marginLeft: "10px",
              paddingTop: "20px",
              paddingRight: "40px",
              paddingBottom: "20px",
              paddingLeft: "40px",
              textShadow: "none",
              letterSpacing: "0.5px",
              lineHeight: "1.3",
              display: "block",
              width: "auto",
              maxWidth: "90%"
            }}>
            Ammattitaitoista ikkunanpesua Espoossa joustavalla aikataululla
          </h1>
          <button
            onClick={() => document.getElementById('hintalaskuri')?.scrollIntoView({ behavior: 'smooth' })}
            className="mt-4 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition transform hover:scale-105"
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "rgb(255, 255, 255)",
              textAlign: "center",
              backgroundColor: "#16a34a",
              borderRadius: "50px",
              margin: "0px",
              padding: "16px 32px",
              boxShadow: "0 4px 15px rgba(22, 163, 74, 0.4)",
              border: "2px solid white",
              display: "block",
              width: "auto"
            }}>
            Laske hinta & varaa aika
          </button>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <form id="Tilaa ikkunanpesu" onSubmit={handleSubmit} className="space-y-8">
          {/* Esittely */}
          <section
            className="bg-blue-200 text-blue-900 rounded-2xl shadow-lg p-8 md:p-12 w-full"
            style={{
              background: "linear-gradient(135deg, #dbeafe 0%, #fef3c7 60%, #d1fae5 100%)",
              fontSize: "16px",
              fontWeight: "400",
              color: "rgb(30, 58, 138)",
              textAlign: "start",
              borderRadius: "16px",
              margin: "0px",
              padding: "52px 48px",
              marginTop: "0px",
              marginRight: "0px",
              marginBottom: "0px",
              marginLeft: "0px",
              paddingTop: "45px",
              paddingRight: "48px",
              paddingBottom: "45px",
              paddingLeft: "48px"
            }}>
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-1">
                <h2
                  className="text-3xl font-bold text-blue-900 mb-6 flex items-center gap-3"
                  style={{
                    color: "#14532d"
                  }}>
                  Joonan Ikkunanpesu
                </h2>
                <div className="text-blue-800 space-y-4">
                  <p
                    style={{
                      fontWeight: "normal",
                      color: "rgb(0, 0, 0)",
                      fontSize: "19px",
                      lineHeight: "1.6"
                    }}>Olen 19-vuotias nuori yrittäjä, joka tarjoaa laadukasta sekä lämminhenkistä ikkunanpesua kilpailukykyiseen hintaan Espoon alueella. Ikkunoiden pesussa käytämme ainoastaan markkinoiden kilpailukykyisimpiä varusteita parhaan lopputuloksen takaamiseksi.</p>
                  <p
                    style={{
                      fontSize: "19px",
                      color: "rgb(0, 0, 0)",
                      lineHeight: "1.6",
                      fontWeight: "normal"
                    }}>Työstä on mahdollista saada kotitalousvähennys (-35%), mikä tekee palvelusta entistäkin edullisemman juuri sinulle!</p>
                </div>
              </div>
              <div
                className="w-full md:w-1/2 lg:w-5/12"
                style={{
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "rgb(30, 58, 138)",
                  textAlign: "start",
                  backgroundColor: "rgba(0, 0, 0, 0)",
                  borderRadius: "0px",
                  margin: "50px 0px",
                  padding: "0px",
                  marginTop: "0px",
                  marginRight: "0px",
                  marginBottom: "0px",
                  marginLeft: "0px",
                  paddingTop: "0px",
                  paddingRight: "0px",
                  paddingBottom: "0px",
                  paddingLeft: "0px"
                }}>
                <div className="aspect-video rounded-xl overflow-hidden shadow-xl mb-4">
                  <iframe
                    src="https://www.youtube.com/embed/oF3iAe0LOBI?autoplay=1&mute=1&loop=1"
                    className="w-full h-full"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="Esittelyvideo"
                  />
                </div>
                <button
                  onClick={() => document.getElementById('Tilaa ikkunanpesu')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full mt-6 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition transform hover:scale-105"
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "rgb(255, 255, 255)",
                    textAlign: "center",
                    backgroundColor: "rgb(22, 163, 74)",
                    borderRadius: "8px",
                    margin: "5px 10px 0px 0px",
                    padding: "12px 20px",
                    marginTop: "5px",
                    marginRight: "0px",
                    marginBottom: "0px",
                    marginLeft: "0px",
                    paddingTop: "12px",
                    paddingRight: "20px",
                    paddingBottom: "12px",
                    paddingLeft: "20px"
                  }}>
                  Ota yhteyttä ja tilaa ikkunanpesu!
                </button>
              </div>
            </div>
          </section>

          {/* Asiakaspalautteita */}
          <section className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl shadow-lg p-8 md:p-12 mt-8 w-full">
            <h2
              className="text-3xl font-bold text-green-900 mb-8 text-center"
              style={{
                color: "#14532d"
              }}>Mitä asiakkaat sanovat</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-1 mb-3"><span className="text-yellow-400">★★★★★</span></div>
                <p className="text-gray-700 mb-4 italic">"Erinomainen palvelu! Ikkunat ovat kuin uudet ja työn jälki oli todella siisti. Suosittelen lämpimästi!"</p>
                <p className="font-semibold text-green-700">- Maria L., Espoo</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-1 mb-3"><span className="text-yellow-400">★★★★★</span></div>
                <p className="text-gray-700 mb-4 italic">"Nuoret tulivat ja tekivät työnsä huolellisesti. Hinta-laatusuhde kohdallaan."</p>
                <p className="font-semibold text-green-700">- Petri K., Tapiola</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-1 mb-3"><span className="text-yellow-400">★★★★★</span></div>
                <p className="text-gray-700 mb-4 italic">"Tilasin ikkunanpesun ja olen erittäin tyytyväinen. Nopeasti, edullisesti ja hyvällä lopputuloksella!"</p>
                <p className="font-semibold text-green-700">- Satu M., Leppävaara</p>
              </div>
            </div>
          </section>

          {/* Window Services */}
          <section id="hintalaskuri" className="bg-blue-50 rounded-2xl shadow-lg p-6 md:p-8">
            <h2 className="text-2xl font-semibold text-blue-900 mb-6">
              Ikkunanpesun hintalaskuri
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Yksilasinen ikkuna (2 pintaa) */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <img src={imgYksilasinenIkkuna} alt="Yksilasinen ikkuna" className="w-16 h-16 rounded-lg object-contain" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Yksilasinen ikkuna</h3>
                    <p className="text-sm text-gray-500">2 pintaa</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{PRICES.yksilasinenIkkuna}€ <span className="text-sm font-normal text-gray-500">/kpl</span></span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateField('yksilasinenIkkuna', Math.max(0, formData.yksilasinenIkkuna - 1))}
                      className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-semibold text-lg">{formData.yksilasinenIkkuna}</span>
                    <button
                      type="button"
                      onClick={() => updateField('yksilasinenIkkuna', formData.yksilasinenIkkuna + 1)}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Kaksilasinen ikkuna (4 pintaa) */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <img src={imgKaksilasinenIkkuna} alt="Kaksilasinen ikkuna" className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Kaksilasinen ikkuna</h3>
                    <p className="text-sm text-gray-500">4 pintaa</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{PRICES.kaksilasinenIkkuna}€ <span className="text-sm font-normal text-gray-500">/kpl</span></span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateField('kaksilasinenIkkuna', Math.max(0, formData.kaksilasinenIkkuna - 1))}
                      className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-semibold text-lg">{formData.kaksilasinenIkkuna}</span>
                    <button
                      type="button"
                      onClick={() => updateField('kaksilasinenIkkuna', formData.kaksilasinenIkkuna + 1)}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Kolmilasinen ikkuna (6 pintaa) */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <img src={imgKolmilasinenIkkuna} alt="Kolmilasinen ikkuna" className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Kolmilasinen ikkuna</h3>
                    <p className="text-sm text-gray-500">6 pintaa</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{PRICES.kolmilasinenIkkuna}€ <span className="text-sm font-normal text-gray-500">/kpl</span></span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateField('kolmilasinenIkkuna', Math.max(0, formData.kolmilasinenIkkuna - 1))}
                      className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-semibold text-lg">{formData.kolmilasinenIkkuna}</span>
                    <button
                      type="button"
                      onClick={() => updateField('kolmilasinenIkkuna', formData.kolmilasinenIkkuna + 1)}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Tuuletusikkuna (2 pintaa) */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <img src={imgTuuletusikkuna} alt="Tuuletusikkuna" className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Tuuletusikkuna</h3>
                    <p className="text-sm text-gray-500">2 pintaa</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{PRICES.tuuletusikkuna}€ <span className="text-sm font-normal text-gray-500">/kpl</span></span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateField('tuuletusikkuna', Math.max(0, formData.tuuletusikkuna - 1))}
                      className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-semibold text-lg">{formData.tuuletusikkuna}</span>
                    <button
                      type="button"
                      onClick={() => updateField('tuuletusikkuna', formData.tuuletusikkuna + 1)}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Kaksilasinen tuuletusikkuna (4 pintaa) */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <img src={imgTuuletusikkuna} alt="Kaksilasinen tuuletusikkuna" className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Kaksilasinen tuuletusikkuna</h3>
                    <p className="text-sm text-gray-500">4 pintaa</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{PRICES.kaksilasinenTuuletusikkuna}€ <span className="text-sm font-normal text-gray-500">/kpl</span></span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateField('kaksilasinenTuuletusikkuna', Math.max(0, formData.kaksilasinenTuuletusikkuna - 1))}
                      className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-semibold text-lg">{formData.kaksilasinenTuuletusikkuna}</span>
                    <button
                      type="button"
                      onClick={() => updateField('kaksilasinenTuuletusikkuna', formData.kaksilasinenTuuletusikkuna + 1)}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Ruutuikkuna (2 pintaa) */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <img src={imgRuutuikkuna} alt="Ruutuikkuna" className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Ruutuikkuna</h3>
                    <p className="text-sm text-gray-500">2 pintaa</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{PRICES.ruutuikkuna}€ <span className="text-sm font-normal text-gray-500">/kpl</span></span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateField('ruutuikkuna', Math.max(0, formData.ruutuikkuna - 1))}
                      className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-semibold text-lg">{formData.ruutuikkuna}</span>
                    <button
                      type="button"
                      onClick={() => updateField('ruutuikkuna', formData.ruutuikkuna + 1)}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Yksilasinen ikkunaryhma (2 pintaa) */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <img src={imgIkkunaryhma} alt="Yksilasinen ikkunaryhmä" className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Yksilasinen ikkunaryhmä</h3>
                    <p className="text-sm text-gray-500">2 pintaa</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{PRICES.ikkunaryhma}€ <span className="text-sm font-normal text-gray-500">/kpl</span></span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateField('ikkunaryhma', Math.max(0, formData.ikkunaryhma - 1))}
                      className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-semibold text-lg">{formData.ikkunaryhma}</span>
                    <button
                      type="button"
                      onClick={() => updateField('ikkunaryhma', formData.ikkunaryhma + 1)}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Ovi lasilla (2 pintaa) */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <img src={imgOviLasilla} alt="Ovi lasilla" className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Ovi lasilla</h3>
                    <p className="text-sm text-gray-500">2 pintaa</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{PRICES.oviLasilla}€ <span className="text-sm font-normal text-gray-500">/kpl</span></span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateField('oviLasilla', Math.max(0, formData.oviLasilla - 1))}
                      className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-semibold text-lg">{formData.oviLasilla}</span>
                    <button
                      type="button"
                      onClick={() => updateField('oviLasilla', formData.oviLasilla + 1)}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Kääntyvä parvekelasi (2 pintaa) */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <img src={imgKanTyvaParvekelasi} alt="Kääntyvä parvekelasi" className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Kääntyvä parvekelasi</h3>
                    <p className="text-sm text-gray-500">2 pintaa</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{PRICES.kanTyvaParvekelasi}€ <span className="text-sm font-normal text-gray-500">/kpl</span></span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateField('kanTyvaParvekelasi', Math.max(0, formData.kanTyvaParvekelasi - 1))}
                      className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-xl hover:bg-blue-200 transition"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-semibold text-lg">{formData.kanTyvaParvekelasi}</span>
                    <button
                      type="button"
                      onClick={() => updateField('kanTyvaParvekelasi', formData.kanTyvaParvekelasi + 1)}
                      className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xl hover:bg-blue-700 transition"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Kesto ja hinta-arvio */}
          <section className="bg-blue-50 rounded-2xl shadow-lg p-6 md:p-8 w-full">
            <h2 className="text-2xl font-semibold text-blue-900 mb-6 flex items-center gap-3">
              <span
                className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-blue-600"
                style={{
                  backgroundColor: "#ffffff"
                }}>⏱️</span>
              Kesto ja hinta-arvio
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {/* Box 1: Hinta */}
              <div
                className="bg-white rounded-xl p-4 shadow-sm"
                style={{
                  backgroundColor: "#0060d1"
                }}>
                <h3
                  className="text-lg font-semibold text-blue-900 mb-2"
                  style={{
                    fontSize: "20px",
                    color: "rgb(255, 255, 255)"
                  }}>Ikkunanpesun hinta-arvio</h3>
                <p
                  className="text-3xl font-bold text-blue-600"
                  style={{
                    fontSize: "32px",
                    fontWeight: "700",
                    color: "rgb(255, 255, 255)",
                    textAlign: "start",
                    backgroundColor: "rgba(0, 0, 0, 0)",
                    borderRadius: "0px",
                    margin: "0px",
                    padding: "0px 95px",
                    marginTop: "0px",
                    marginRight: "0px",
                    marginBottom: "0px",
                    marginLeft: "0px",
                    paddingTop: "0px",
                    paddingRight: "80px",
                    paddingBottom: "0px",
                    paddingLeft: "80px"
                  }}>{calculateTotal()}€</p>
              </div>
              
              {/* Box 2: Kotitalousvähennys */}
              <div
                className="bg-white rounded-xl p-4 shadow-sm"
                style={{
                  backgroundColor: "#0060d1"
                }}>
                <h3
                  className="text-lg font-semibold text-green-900 mb-2"
                  style={{
                    fontSize: "20px",
                    color: "rgb(255, 255, 255)"
                  }}>Kotitalousvähennyksen jälkeen</h3>
                <p
                  className="text-3xl font-bold text-blue-600"
                  style={{
                    color: "rgb(255, 255, 255)",
                    fontSize: "32px",
                    fontWeight: "700",
                    textAlign: "start",
                    backgroundColor: "rgba(0, 0, 0, 0)",
                    borderRadius: "0px",
                    margin: "0px",
                    padding: "0px 95px",
                    marginTop: "0px",
                    marginRight: "0px",
                    marginBottom: "0px",
                    marginLeft: "0px",
                    paddingTop: "0px",
                    paddingRight: "80px",
                    paddingBottom: "0px",
                    paddingLeft: "80px"
                  }}>{Math.round(calculateTotal() * 0.65)}€</p>
                <p
                  className="text-xs text-gray-500 mt-1"
                  style={{
                    color: "#ffffff"
                  }}>{calculateTotal() > 0 ? calculateTotal() : ''}</p>
              </div>
              
              {/* Box 3: Kesto */}
              <div
                className="bg-white rounded-xl p-4 shadow-sm"
                style={{
                  backgroundColor: "#0060d1"
                }}>
                <h3
                  className="text-lg font-semibold text-blue-900 mb-2"
                  style={{
                    fontSize: "20px",
                    color: "rgb(255, 255, 255)"
                  }}>Työn arvioitu kesto (min)</h3>
                <p
                  className="text-3xl font-bold text-blue-600"
                  style={{
                    fontSize: "32px",
                    fontWeight: "700",
                    color: "rgb(255, 255, 255)",
                    textAlign: "start",
                    backgroundColor: "rgba(0, 0, 0, 0)",
                    borderRadius: "0px",
                    margin: "0px",
                    padding: "0px 73px",
                    marginTop: "0px",
                    marginRight: "0px",
                    marginBottom: "0px",
                    marginLeft: "0px",
                    paddingTop: "0px",
                    paddingRight: "65px",
                    paddingBottom: "0px",
                    paddingLeft: "65px"
                  }}>{calculateDuration()}</p>
              </div>
            </div>

          </section>

          {/* Customer Information */}
          <section
            className="bg-green-50 rounded-2xl shadow-lg p-6 md:p-8 w-full"
            style={{
              backgroundColor: "#f8fdf1"
            }}>
            <h2 className="text-2xl font-semibold text-green-900 mb-2 flex items-center gap-3">
              Tilaa ikkunanpesu!</h2>
            <p className="text-gray-500 mb-6">Täytä tietosi ja olemme sinuun yhteydessä vain muutamassa tunnissa. Myös valitsemiesi ikkunoiden määrät näkyvät meille.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nimi</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  required
                  placeholder="Etunimi Sukunimi"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Puhelin</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  required
                  placeholder="040 123 4567"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Osoite</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  required
                  placeholder="Katuosoite, Postinumero"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Kaupunki</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  required
                  placeholder="Kaupunki"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Sähköposti</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  required
                  placeholder="sahkoposti@esimerkki.fi"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Vahvista sähköposti</label>
                <input
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  required
                  placeholder="sahkoposti@esimerkki.fi"
                  className={`w-full px-4 py-3 rounded-xl border focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition ${emailError ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                />
                {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Siivouksen aloitusaikaan liittyvät toiveet tai muut huomiot</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  rows={3}
                  placeholder="Esim. Työmaa-alue, erityisohjeet tms."
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                />
              </div>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 px-6 py-2 bg-green-500 text-white font-medium text-sm rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:text-gray-500 transition"
            >
              {isSubmitting ? 'Lähetetään...' : 'Lähetä lomake'}
            </button>
          </section>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">✓</span>
                <div>
                  <p className="font-semibold">Tilaus lähetetty onnistuneesti!</p>
                  <p className="text-sm">Olemme vastaanottaneet tilauksesi. Olemme sinuun yhteydessä pian.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">✗</span>
                <div>
                  <p className="font-semibold">Virhe tilauksen lähetyksessä</p>
                  <p className="text-sm">Yritä myöhemmin uudelleen tai ota meihin yhteyttä puhelimitse.</p>
                </div>
              </div>
            </div>
          )}
        </form>
      </main>
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-xl font-semibold mb-2">Joonan Ikkunanpesu</h3>
          <p className="text-gray-400 text-sm">Ammattitaitoista ikkunanpesua Espoossa</p>
          <div className="mt-4 space-y-1 text-sm text-gray-300">
            <p>Y-tunnus: 3351587-9</p>
            <p>Puh: <a href="tel:0453176464" className="text-blue-400 hover:text-blue-300">0453176464</a></p>
            <p>Sähköposti: <a href="mailto:soininjoona@gmail.com" className="text-blue-400 hover:text-blue-300">soininjoona@gmail.com</a></p>
          </div>
          <p className="text-gray-500 text-xs mt-4">© 2026 Joonan Ikkunanpesu. Kaikki oikeudet pidätetty.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
